import { Link } from 'react-router-dom'
import { FileText, Shield, Building, Scale, Clock, Mail } from 'lucide-react'
import '../styles/LegalPage.css'

export default function LegalPage() {
  const lastUpdated = '15 Janvier 2024'
  
  return (
    <div className="legal-page">
      {/* Hero Section */}
      <section className="legal-hero">
        <div className="container">
          <div className="legal-hero-content">
            <div className="legal-icon">
              <Scale size={48} />
            </div>
            <h1 className="legal-title">Mentions Légales</h1>
            <p className="legal-subtitle">
              Informations légales concernant UltimaTag
            </p>
            <div className="legal-update">
              <Clock size={16} />
              <span>Dernière mise à jour : {lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="legal-content">
        <div className="container">
          <div className="legal-sections">
            {/* Informations de l'entreprise */}
            <section className="legal-section">
              <h2 className="section-title">
                <Building size={24} />
                Informations sur l'entreprise
              </h2>
              
              <div className="info-grid">
                <div className="info-item">
                  <strong>Dénomination sociale :</strong>
                  <span>ULTIMATAG SARL</span>
                </div>
                <div className="info-item">
                  <strong>Forme juridique :</strong>
                  <span>Société à Responsabilité Limitée (SARL)</span>
                </div>
                <div className="info-item">
                  <strong>Capital social :</strong>
                  <span>1 000 000 FCFA</span>
                </div>
                <div className="info-item">
                  <strong>RCCM :</strong>
                  <span>CI-ABJ-2024-B-12345</span>
                </div>
                <div className="info-item">
                  <strong>NIF :</strong>
                  <span>M12345678901234P</span>
                </div>
                <div className="info-item">
                  <strong>Siège social :</strong>
                  <span>Abidjan, Plateau - Immeuble CCIA, 24ème étage</span>
                </div>
                <div className="info-item">
                  <strong>Téléphone :</strong>
                  <span>+225 XX XX XX XX</span>
                </div>
                <div className="info-item">
                  <strong>Email :</strong>
                  <span>legal@ultimatag.com</span>
                </div>
                <div className="info-item">
                  <strong>Directeur de publication :</strong>
                  <span>M. Jean DUPONT</span>
                </div>
                <div className="info-item">
                  <strong>Hébergeur :</strong>
                  <span>Firebase (Google Cloud Platform)</span>
                </div>
              </div>
            </section>

            {/* Propriété intellectuelle */}
            <section className="legal-section">
              <h2 className="section-title">
                <FileText size={24} />
                Propriété intellectuelle
              </h2>
              
              <div className="section-content">
                <p>
                  L'ensemble de ce site relève de la législation ivoirienne et internationale 
                  sur le droit d'auteur et la propriété intellectuelle. Tous les droits de 
                  reproduction sont réservés, y compris pour les documents téléchargeables et 
                  les représentations iconographiques et photographiques.
                </p>
                
                <h3 className="subsection-title">Marques déposées</h3>
                <ul className="legal-list">
                  <li>
                    <strong>"UltimaTag"</strong> est une marque déposée
                  </li>
                  <li>
                    <strong>Le logo UltimaTag</strong> est une marque déposée
                  </li>
                  <li>
                    <strong>"UT"</strong> (sigle) est une marque déposée
                  </li>
                </ul>
                
                <h3 className="subsection-title">Contenu protégé</h3>
                <p>
                  Sauf autorisation préalable écrite, toute reproduction, représentation, 
                  adaptation, traduction et/ou modification, partielle ou intégrale, ou 
                  transfert sur un autre site sont interdits.
                </p>
              </div>
            </section>

            {/* Protection des données */}
            <section className="legal-section">
              <h2 className="section-title">
                <Shield size={24} />
                Protection des données personnelles
              </h2>
              
              <div className="section-content">
                <p>
                  Conformément à la loi n°2013-450 du 19 juin 2013 relative à la protection 
                  des données à caractère personnel, vous disposez d'un droit d'accès, de 
                  rectification et de suppression des données vous concernant.
                </p>
                
                <div className="data-points">
                  <div className="data-point">
                    <h3>Droit d'accès</h3>
                    <p>Vous pouvez accéder à vos données personnelles à tout moment</p>
                  </div>
                  <div className="data-point">
                    <h3>Droit de rectification</h3>
                    <p>Vous pouvez demander la correction de vos données</p>
                  </div>
                  <div className="data-point">
                    <h3>Droit à l'oubli</h3>
                    <p>Vous pouvez demander la suppression de vos données</p>
                  </div>
                  <div className="data-point">
                    <h3>Consentement</h3>
                    <p>Vos données ne sont utilisées qu'avec votre consentement</p>
                  </div>
                </div>
                
                <p>
                  Pour exercer ces droits, veuillez contacter notre délégué à la protection 
                  des données à l'adresse : <strong>dpo@ultimatag.com</strong>
                </p>
              </div>
            </section>

            {/* Limitation de responsabilité */}
            <section className="legal-section">
              <h2 className="section-title">Limitation de responsabilité</h2>
              
              <div className="section-content">
                <p>
                  UltimaTag s'efforce d'assurer au mieux de ses possibilités l'exactitude 
                  et la mise à jour des informations diffusées sur ce site, dont il se réserve 
                  le droit de corriger, à tout moment et sans préavis, le contenu.
                </p>
                
                <p>
                  Toutefois, UltimaTag ne peut garantir l'exactitude, la précision ou 
                  l'exhaustivité des informations mises à disposition sur ce site. En 
                  conséquence, UltimaTag décline toute responsabilité :
                </p>
                
                <ul className="legal-list">
                  <li>Pour toute interruption du site</li>
                  <li>Pour toute survenance de bogues</li>
                  <li>Pour toute inexactitude ou omission portant sur des informations disponibles sur le site</li>
                  <li>Pour tous dommages résultant d'une intrusion frauduleuse d'un tiers</li>
                  <li>Pour tous dommages directs ou indirects, quelles qu'en soient les causes, origines, natures ou conséquences</li>
                </ul>
              </div>
            </section>

            {/* Loi applicable */}
            <section className="legal-section">
              <h2 className="section-title">Loi applicable et juridiction compétente</h2>
              
              <div className="section-content">
                <p>
                  Les présentes mentions légales sont soumises au droit ivoirien. 
                  En cas de litige, et à défaut d'accord amiable, les tribunaux 
                  compétents seront ceux du siège social d'UltimaTag.
                </p>
                
                <div className="jurisdiction-info">
                  <div className="jurisdiction-item">
                    <strong>Loi applicable :</strong>
                    <span>Droit ivoirien</span>
                  </div>
                  <div className="jurisdiction-item">
                    <strong>Langue des contrats :</strong>
                    <span>Français</span>
                  </div>
                  <div className="jurisdiction-item">
                    <strong>Juridiction :</strong>
                    <span>Tribunal de commerce d'Abidjan</span>
                  </div>
                  <div className="jurisdiction-item">
                    <strong>Médiation :</strong>
                    <span>Médiateur de la consommation (CI)</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact légal */}
            <section className="legal-contact">
              <h2 className="contact-title">Contact légal</h2>
              
              <div className="contact-cards">
                <div className="contact-card">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <h3>Service juridique</h3>
                  <p>legal@ultimatag.com</p>
                  <small>Traitement sous 48h</small>
                </div>
                
                <div className="contact-card">
                  <div className="contact-icon">
                    <Shield size={24} />
                  </div>
                  <h3>Protection des données</h3>
                  <p>dpo@ultimatag.com</p>
                  <small>RGPD & conformité</small>
                </div>
                
                <div className="contact-card">
                  <div className="contact-icon">
                    <Building size={24} />
                  </div>
                  <h3>Siège social</h3>
                  <p>Abidjan, Plateau</p>
                  <small>Sur rendez-vous uniquement</small>
                </div>
              </div>
            </section>
          </div>

          {/* Quick Links */}
          <div className="legal-links">
            <h3>Documents légaux</h3>
            <div className="links-grid">
              <Link to="/privacy" className="legal-link">
                <Shield size={16} />
                <span>Politique de confidentialité</span>
              </Link>
              
              <Link to="/terms" className="legal-link">
                <FileText size={16} />
                <span>Conditions d'utilisation</span>
              </Link>
              
              <Link to="/cookies" className="legal-link">
                <FileText size={16} />
                <span>Politique des cookies</span>
              </Link>
              
              <Link to="/cgv" className="legal-link">
                <FileText size={16} />
                <span>Conditions générales de vente</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}